import { useEffect, useState } from 'react';

import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import { commonStrings, lineStrings } from 'constants/strings';

type BulkFriendLimitModalProps = {
  open: boolean;
  selectedCount: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (friendLimit: number) => void;
};

export function BulkFriendLimitModal({
  open,
  selectedCount,
  isSubmitting,
  onClose,
  onSubmit,
}: BulkFriendLimitModalProps) {
  const strings = lineStrings.management.bulk.friendLimitModal;
  const [friendLimit, setFriendLimit] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setFriendLimit('');
      setError(undefined);
    }
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    const trimmed = friendLimit.trim();
    const parsed = Number(trimmed);
    if (trimmed === '' || !Number.isInteger(parsed) || parsed < 0) {
      setError(lineStrings.management.bulk.errors.invalidFriendLimit);
      return;
    }
    setError(undefined);
    onSubmit(parsed);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="grid gap-5">
        <div className="grid gap-1.5">
          <h2 className="text-lg font-semibold text-ink">{strings.title}</h2>
          <p className="text-sm text-muted">
            {strings.description.replace('{{count}}', String(selectedCount))}
          </p>
        </div>
        <label className="grid gap-2 text-xs font-semibold text-ink">
          {strings.label}
          <Input
            type="number"
            min={0}
            step={1}
            name="bulkFriendLimit"
            placeholder={strings.placeholder}
            autoComplete="off"
            disabled={isSubmitting}
            value={friendLimit}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'bulkFriendLimit-error' : undefined}
            onChange={(event) => setFriendLimit(event.target.value)}
          />
          {error && (
            <span
              id="bulkFriendLimit-error"
              role="alert"
              className="text-[11px] font-medium text-danger"
            >
              {error}
            </span>
          )}
        </label>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={handleClose}
          >
            {commonStrings.cancel}
          </Button>
          <Button
            type="button"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {strings.submit}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
